import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { qs } from "../lib/api.ts";
import { useApi, relativeTime, titleCase } from "../lib/hooks.ts";
import type { DeviceGroup, Paged, Device } from "../lib/types.ts";
import { Badge, Card, Empty, ErrorAlert, Pager, Spinner } from "../components/ui.tsx";

const COLUMNS: Array<{ key: string; label: string }> = [
  { key: "name", label: "Dispositivo" },
  { key: "user", label: "Usuario" },
  { key: "platform", label: "Plataforma" },
  { key: "status", label: "Status" },
  { key: "lastSeen", label: "Ultimo contato" },
  { key: "riskScore", label: "Risco" },
];

const STATUSES = ["online", "offline", "missing", "frozen", "wiped", "retired"];
const PLATFORMS = ["Windows", "macOS", "Linux", "ChromeOS"];

export function DevicesPage() {
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("q") ?? "");

  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "";
  const platform = params.get("platform") ?? "";
  const groupId = params.get("groupId") ?? "";
  const sort = params.get("sort") ?? "name";
  const dir = params.get("dir") ?? "asc";
  const page = Number(params.get("page") ?? 1);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (draft !== q) update({ q: draft, page: 1 });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  function update(patch: Record<string, string | number | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "" || value === undefined) next.delete(key);
      else next.set(key, String(value));
    }
    setParams(next, { replace: true });
  }

  const path = useMemo(
    () =>
      `/api/v3/devices${qs({ q, status, platform, groupId, sort, dir, page, limit: 25 })}`,
    [q, status, platform, groupId, sort, dir, page],
  );

  const devices = useApi<Paged<Device>>(path, [], { poll: 5000 });
  const groups = useApi<{ data: DeviceGroup[] }>("/api/v3/device-groups");

  const total = devices.data?.meta.total ?? 0;

  function toggleSort(key: string) {
    if (sort === key) update({ dir: dir === "asc" ? "desc" : "asc", page: 1 });
    else update({ sort: key, dir: "asc", page: 1 });
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Dispositivos</h1>
          <p className="subtle">
            {devices.loading ? "Carregando inventario..." : `${total} dispositivos no inventario`}
          </p>
        </div>
        <span className="spacer" />
        <button className="btn" onClick={() => devices.reload()}>
          Atualizar
        </button>
      </div>

      <Card>
        <div className="toolbar">
          <div className="grow">
            <input
              className="input"
              placeholder="Buscar por nome, host, serie ou usuario..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Busca"
            />
          </div>
          <select className="select" style={{ width: 160 }} value={status} onChange={(e) => update({ status: e.target.value, page: 1 })} aria-label="Status">
            <option value="">Todos os status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </select>
          <select className="select" style={{ width: 160 }} value={platform} onChange={(e) => update({ platform: e.target.value, page: 1 })} aria-label="Plataforma">
            <option value="">Todas plataformas</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select className="select" style={{ width: 220 }} value={groupId} onChange={(e) => update({ groupId: e.target.value, page: 1 })} aria-label="Grupo">
            <option value="">Todos os grupos</option>
            {(groups.data?.data ?? []).map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name} ({g.deviceCount})
              </option>
            ))}
          </select>
          {q || status || platform || groupId ? (
            <button
              className="btn ghost"
              onClick={() => {
                setDraft("");
                setParams(new URLSearchParams(), { replace: true });
              }}
            >
              Limpar filtros
            </button>
          ) : null}
        </div>
      </Card>

      <Card tight>
        {devices.error ? <div style={{ padding: 16 }}><ErrorAlert message={devices.error.message} /></div> : null}
        {devices.loading && !devices.data ? (
          <div className="empty"><Spinner /></div>
        ) : !devices.data || devices.data.data.length === 0 ? (
          <Empty>Nenhum dispositivo corresponde aos filtros.</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    {COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className="sortable"
                        onClick={() => toggleSort(col.key)}
                        title="Ordenar"
                      >
                        {col.label}
                        {sort === col.key ? (dir === "asc" ? " ▲" : " ▼") : ""}
                      </th>
                    ))}
                    <th>Politica</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.data.data.map((d) => (
                    <tr key={d.uid}>
                      <td>
                        <Link to={`/devices/${d.uid}`} className="cell-title">
                          {d.name}
                        </Link>
                        <div className="cell-sub">
                          {d.make} {d.model} · {d.serial}
                        </div>
                      </td>
                      <td>
                        <div>{d.user.name}</div>
                        <div className="cell-sub">{d.user.department}</div>
                      </td>
                      <td>
                        <div>{d.platform}</div>
                        <div className="cell-sub">{d.osVersion}</div>
                      </td>
                      <td>
                        <Badge value={d.status} />
                      </td>
                      <td className="cell-sub">{relativeTime(d.lastSeen)}</td>
                      <td>
                        <strong style={{ color: d.riskScore >= 70 ? "var(--danger)" : undefined }}>
                          {d.riskScore}
                        </strong>
                      </td>
                      <td className="cell-sub">{d.group?.name ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager meta={devices.data.meta} page={page} onPage={(next) => update({ page: next })} />
          </>
        )}
      </Card>
    </div>
  );
}
